import { openDB, DBSchema, IDBPDatabase } from 'idb';

export interface QueuedAction {
  id: string;
  type: 'create' | 'update' | 'delete';
  entity: 'opportunity' | 'contact' | 'company' | 'email' | 'task' | 'lead';
  data: any;
  timestamp: number;
  retryCount: number;
  status: 'pending' | 'processing' | 'failed';
  error?: string;
}

interface OfflineQueueDB extends DBSchema {
  actions: {
    key: string;
    value: QueuedAction;
    indexes: { 'by-status': string; 'by-entity': string; 'by-timestamp': number };
  };
}

class OfflineQueue {
  private db: IDBPDatabase<OfflineQueueDB> | null = null;
  private readonly DB_NAME = 'buildcrm-offline-queue';
  private readonly DB_VERSION = 1;

  async init() {
    if (this.db) return this.db;

    this.db = await openDB<OfflineQueueDB>(this.DB_NAME, this.DB_VERSION, {
      upgrade(db) {
        const store = db.createObjectStore('actions', { keyPath: 'id' });
        store.createIndex('by-status', 'status');
        store.createIndex('by-entity', 'entity');
        store.createIndex('by-timestamp', 'timestamp');
      },
    });

    return this.db;
  }

  async enqueue(action: Omit<QueuedAction, 'id' | 'timestamp' | 'retryCount' | 'status'>): Promise<string> {
    const db = await this.init();
    const id = `${action.entity}-${action.type}-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    
    const queuedAction: QueuedAction = {
      ...action,
      id,
      timestamp: Date.now(),
      retryCount: 0,
      status: 'pending',
    };

    await db.add('actions', queuedAction);
    
    // Trigger background sync if available
    if ('serviceWorker' in navigator && 'sync' in ServiceWorkerRegistration.prototype) {
      try {
        const registration = await navigator.serviceWorker.ready;
        await (registration as any).sync.register('sync-offline-queue');
      } catch (error) {
        console.log('Background sync not available:', error);
      }
    }

    return id;
  }

  async getAll(): Promise<QueuedAction[]> {
    const db = await this.init();
    return db.getAll('actions');
  }

  async getPending(): Promise<QueuedAction[]> {
    const db = await this.init();
    return db.getAllFromIndex('actions', 'by-status', 'pending');
  }

  async getByEntity(entity: string): Promise<QueuedAction[]> {
    const db = await this.init();
    return db.getAllFromIndex('actions', 'by-entity', entity);
  }

  async get(id: string): Promise<QueuedAction | undefined> {
    const db = await this.init();
    return db.get('actions', id);
  }

  async update(id: string, updates: Partial<QueuedAction>): Promise<void> {
    const db = await this.init();
    const action = await db.get('actions', id);
    if (!action) return;

    const updatedAction = { ...action, ...updates };
    await db.put('actions', updatedAction);
  }

  async remove(id: string): Promise<void> {
    const db = await this.init();
    await db.delete('actions', id);
  }

  async clear(): Promise<void> {
    const db = await this.init();
    await db.clear('actions');
  }

  async count(): Promise<number> {
    const db = await this.init();
    return db.count('actions');
  }

  async incrementRetry(id: string): Promise<void> {
    const db = await this.init();
    const action = await db.get('actions', id);
    if (!action) return;

    action.retryCount += 1;
    if (action.retryCount >= 3) {
      action.status = 'failed';
    }
    await db.put('actions', action);
  }

  async markAsProcessing(id: string): Promise<void> {
    await this.update(id, { status: 'processing' });
  }

  async markAsFailed(id: string, error: string): Promise<void> {
    await this.update(id, { status: 'failed', error });
  }
}

export const offlineQueue = new OfflineQueue();
